import type { ReactNode } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { HeartIcon, HistoryIcon } from "lucide-react";
import { Button } from "@ds/ui/button";
import { EmptyState } from "@ds/patterns/empty-state";
import { Skeleton } from "@ds/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@ds/ui/tabs";
import type { UseInfiniteQueryResult, InfiniteData } from "@tanstack/react-query";
import {
  flattenLogPages,
  useBenchmarks,
  useLogActivity,
  useFavouriteLog,
  useSessionLogTrend,
  useSessionLogs,
} from "../logs.queries";
import type {
  LogActivityDayModel,
  SessionLogPageModel,
  SessionLogSummaryModel,
} from "../logs.model";
import { MovementList } from "@/features/movements";
import { SessionLogRow } from "./SessionLogRow";
import { SessionTrend } from "./SessionTrend";

/**
 * HISTORY — handoff Screen 6. Three segmented tabs, and the one that is showing is in the URL.
 *
 * ═══ A BENCHMARK IS A FAVOURITED SESSION ═══
 *
 * Not a second kind of thing: the service answers `/v1/logs/benchmarks` as
 * `/v1/logs?favouritesOnly=true` and says so in its own routes file. So both tabs render the
 * SAME row component over the same shape, and the heart on a row is the only thing that
 * moves a session between them.
 *
 * ═══ A ROW OPENS THE SESSION ═══
 *
 * 🔑 "RUN AGAIN" LIVES ON THAT SCREEN AND NOT ON EVERY ROW, and the reason is the one this
 * file used to give for not building it at all: redrawing at a stored seed does NOT reproduce
 * a workout, because the draw takes the athlete's maxes, ladders and recent history as input
 * and all three move. A button on a row would hand somebody a different session under the name
 * of the one they tapped, with nothing on screen to notice it by. On the session screen it is
 * honest: that screen prints every block before anything starts, so a difference is visible
 * before the athlete commits. See `SessionLogDetail`.
 *
 * ⚠️ THERE ARE NO "THE GIRLS" CARDS. The hi-fi's benchmarks tab opens with a grid of named
 * classics (Fran, Grace…). `api/src/domains/logs/routes.ts` states that those are a
 * different concept entirely — coach-authored and shared across athletes — and would be a
 * different domain. Nothing in this schema holds one, so the grid would be eight cards of
 * invented content.
 */
/** A stable empty list, so a pending fetch does not rebuild the grid's map every render. */
const EMPTY_ACTIVITY: LogActivityDayModel[] = [];

/**
 * THE THREE PANELS, AS A CLOSED VOCABULARY.
 *
 * 🚨 IT IS EXPORTED BECAUSE THE ROUTE VALIDATES AGAINST IT. Which tab is showing lives in the
 * URL — see {@link History} — and a route cannot narrow `?tab=` to something it has no list of.
 * The alternative, a second copy of these three words in `src/routes/`, is two vocabularies
 * that agree until somebody adds a fourth panel.
 */
export const HISTORY_TABS = ["sessions", "benchmarks", "movements"] as const;
export type HistoryTab = (typeof HISTORY_TABS)[number];

export interface HistoryProps {
  /**
   * WHICH PANEL IS SHOWING — owned by the route, not by this component.
   *
   * ═══ 🚨 IT USED TO BE `defaultValue="sessions"`, AND THAT WAS THE BUG ═══
   *
   * Uncontrolled state lives as long as the component. Opening a session or a movement PUSHES
   * a screen, which unmounts this one, so coming back mounted it fresh and it reset to the
   * first tab: an athlete browsing their benchmarks tapped one, closed it, and was returned to
   * Sessions with no idea what they had done wrong. It is a place in the app, so it belongs in
   * the URL, where going back restores it for free and a link can carry it.
   */
  tab: HistoryTab;
  onTabChange: (tab: HistoryTab) => void;
}

export function History({ tab, onTabChange }: HistoryProps) {
  const { t } = useTranslation("logs");
  /**
   * ⚠️ `isFavourite` HERE IS THE STATE BEING MOVED TO, NOT THE ONE THE ROW IS IN. That is the
   * debrief's hook (BUD-289) and its convention, reused rather than duplicated — one heart,
   * one mutation, one name in the report. Passing the row's current state instead is a
   * silent no-op that looks like a broken control.
   */
  const heart = useFavouriteLog();
  const navigate = useNavigate();

  const onToggleBenchmark = (log: SessionLogSummaryModel) =>
    heart.mutate({ id: log.id, isFavourite: !log.isFavourite });
  const onOpen = (log: SessionLogSummaryModel) =>
    void navigate({ to: "/history/$id", params: { id: log.id } });

  return (
    <Tabs value={tab} onValueChange={(value) => onTabChange(value as HistoryTab)}>
      <TabsList>
        <TabsTrigger value="sessions">{t("history.tab.sessions")}</TabsTrigger>
        <TabsTrigger value="benchmarks">{t("history.tab.benchmarks")}</TabsTrigger>
        {/**
         * 🚨 THE THIRD TAB IS THE OTHER AXIS THROUGH THE SAME TRAINING. The first two are lists
         * of DAYS — what happened on Tuesday, and which Tuesdays were worth keeping. This one
         * collapses the days away and asks the question nothing in the product could answer:
         * what has happened to my back squat.
         *
         * ⚠️ IT RENDERS ANOTHER FEATURE'S SCREEN, through that feature's barrel, and passes the
         * navigation in. History does not know how a movement's progression is fetched, drawn
         * or cached — it knows there is a tab and where a row goes.
         */}
        <TabsTrigger value="movements" data-testid="tab-movements">
          {t("history.tab.movements")}
        </TabsTrigger>
      </TabsList>

      <TabsContent value="sessions">
        <SessionsTab onOpen={onOpen} onToggleBenchmark={onToggleBenchmark} />
      </TabsContent>

      <TabsContent value="benchmarks">
        <BenchmarksTab onOpen={onOpen} onToggleBenchmark={onToggleBenchmark} />
      </TabsContent>

      <TabsContent value="movements">
        <MovementList
          onOpen={(item) =>
            void navigate({ to: "/movements/$movement", params: { movement: item.movement } })
          }
        />
      </TabsContent>
    </Tabs>
  );
}

interface TabProps {
  onOpen: (log: SessionLogSummaryModel) => void;
  onToggleBenchmark: (log: SessionLogSummaryModel) => void;
}

function SessionsTab({ onOpen, onToggleBenchmark }: TabProps) {
  const { t } = useTranslation("logs");
  const trend = useSessionLogTrend();
  const activity = useLogActivity();
  const list = useSessionLogs();
  const logs = flattenLogPages(list.data);

  return (
    <div className="flex flex-col gap-5">
      <SessionTrend
        logs={trend.data?.items ?? []}
        activity={activity.data ?? EMPTY_ACTIVITY}
        hasMore={trend.data?.nextCursor != null}
        loading={trend.isPending || activity.isPending}
      />

      <section className="flex flex-col gap-2.5">
        <h2 className="text-eyebrow text-muted-foreground uppercase">{t("history.recent")}</h2>
        <LogList
          query={list}
          logs={logs}
          onOpen={onOpen}
          onToggleBenchmark={onToggleBenchmark}
          empty={
            <EmptyState
              illustration={<HistoryIcon />}
              title={t("history.empty.sessions.title")}
              description={t("history.empty.sessions.body")}
            />
          }
        />
      </section>
    </div>
  );
}

function BenchmarksTab({ onOpen, onToggleBenchmark }: TabProps) {
  const { t } = useTranslation("logs");
  const list = useBenchmarks();

  return (
    <section className="flex flex-col gap-2.5">
      <h2 className="text-eyebrow text-muted-foreground uppercase">{t("history.benchmarks")}</h2>
      <LogList
        query={list}
        logs={flattenLogPages(list.data)}
        onOpen={onOpen}
        onToggleBenchmark={onToggleBenchmark}
        empty={
          <EmptyState
            illustration={<HeartIcon />}
            title={t("history.empty.benchmarks.title")}
            description={t("history.empty.benchmarks.body")}
          />
        }
      />
    </section>
  );
}

/**
 * ONE PAGED LIST, DRAWN THE SAME WAY IN BOTH TABS.
 *
 * A BUTTON RATHER THAN A SCROLL SENTINEL. An infinite list that fetches on scroll is what
 * makes a phone list jump under a thumb mid-flick, and the athlete arriving here is looking
 * for last Tuesday rather than reading to the end of their training life.
 *
 * THE FAILURE MESSAGE IS NOT ANNOUNCED HERE. The query cache reports and announces it once;
 * this line is what the screen shows in the space where the list would be, which is a
 * different job from telling somebody what happened.
 */
function LogList({
  query,
  logs,
  onOpen,
  onToggleBenchmark,
  empty,
}: {
  query: UseInfiniteQueryResult<InfiniteData<SessionLogPageModel>>;
  logs: SessionLogSummaryModel[];
  onOpen: (log: SessionLogSummaryModel) => void;
  onToggleBenchmark: (log: SessionLogSummaryModel) => void;
  empty: ReactNode;
}) {
  const { t } = useTranslation("logs");

  if (query.isPending) {
    return (
      <div className="flex flex-col gap-2">
        <Skeleton className="rounded-tile h-18 w-full" />
        <Skeleton className="rounded-tile h-18 w-full" />
        <Skeleton className="rounded-tile h-18 w-full" />
      </div>
    );
  }

  if (query.isError) {
    return <p className="text-support text-muted-foreground">{t("history.unavailable")}</p>;
  }

  if (logs.length === 0) return <>{empty}</>;

  return (
    <>
      <ul className="flex list-none flex-col gap-2">
        {logs.map((log) => (
          <SessionLogRow
            key={log.id}
            log={log}
            onOpen={onOpen}
            onToggleBenchmark={onToggleBenchmark}
          />
        ))}
      </ul>
      {query.hasNextPage ? (
        <Button
          variant="secondary"
          size="sm"
          className="mt-1 w-full"
          loading={query.isFetchingNextPage}
          loadingLabel={t("history.loadingMore")}
          onClick={() => void query.fetchNextPage()}
        >
          {t("history.more")}
        </Button>
      ) : null}
    </>
  );
}
